import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { roundLabel } from '../utils/wcLabels';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Trophy, ArrowLeft, Target, Clock, Check, X,
  MapPin, Calendar, Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import TeamLogo from '../components/TeamLogo';
import Navbar from '../components/Navbar';
import MatchInsights from '../components/MatchInsights';
import MatchLiveStats from '../components/MatchLiveStats';
import Ladder from '../components/Ladder';
import InPlayBadge from '../components/InPlayBadge';
import FinalLeaderboardCard from '../components/FinalLeaderboardCard';
import OddsRefreshChip from '../components/OddsRefreshChip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDeadline(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Sydney',
  }) + ' AEST';
}

function FinaliseBar({ sport, roundNumber, deadline, onFinalise, finalising, potentialTotal }) {
  const rLabel = roundLabel(sport, roundNumber);
  return (
    <div className="tipster-card mt-6 border-[#007AFF]/30 bg-[#007AFF]/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="finalise-bar">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#007AFF] font-semibold mb-1">
          {sport} {rLabel} — Ready to lock in?
        </div>
        <p className="text-sm text-gray-700">
          Pick a winner for every match{sport === 'WC' ? ' (Draw is only available in group stage; one team must progress in knockouts). Enter an exact score for the Game(s) of the Round' : ' and complete the Game of the Round predictions'}.
          {deadline && <> Deadline: <span className="font-semibold">{formatDeadline(deadline)}</span>.</>}
        </p>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        {potentialTotal != null && (
          <div className="text-right" data-testid="potential-total-block">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Potential</div>
            <div className="text-2xl font-black text-[#37003C] tabular-nums leading-none" data-testid="potential-total-value">
              {potentialTotal.toFixed(2)} <span className="text-xs font-bold text-gray-500">pts</span>
            </div>
          </div>
        )}
        <button
          onClick={onFinalise}
          disabled={finalising}
          className="px-6 py-3 rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold uppercase tracking-wide text-sm disabled:opacity-50"
          data-testid="finalise-tips-btn"
        >
          {finalising ? 'Finalising…' : 'Save & Finalise Tips'}
        </button>
      </div>
    </div>
  );
}

export default function TippingPage() {
  const { sport } = useParams();
  const { user } = useAuth();
  const [fixtures, setFixtures] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [tips, setTips] = useState({});
  const [myTips, setMyTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [finalising, setFinalising] = useState(false);
  const [finaliseStatus, setFinaliseStatus] = useState(null); // {is_finalised, deadline, locked, ...}
  const [teamLogos, setTeamLogos] = useState({});
  const [roundSummary, setRoundSummary] = useState(null);
  const [hoveredFixtureId, setHoveredFixtureId] = useState(null);
  const [worldRankings, setWorldRankings] = useState({}); // WC only: team → #N

  const sportColor = sport === 'AFL' ? '#E00000' : sport === 'NRL' ? '#007A33' : sport === 'EPL' ? '#37003C' : sport === 'AL' ? '#F0423E' : '#4338CA';
  const sportBadgeClass = sport === 'AFL' ? 'tipster-badge-afl' : sport === 'NRL' ? 'tipster-badge-nrl' : sport === 'EPL' ? 'tipster-badge-epl' : sport === 'AL' ? 'tipster-badge-al' : 'tipster-badge-wc';
  const isWC = sport === 'WC';
  const isEPL = sport === 'EPL';
  const isAL = sport === 'AL';
  const isOddsBased = isEPL || isAL;
  // EPL and WC (all rounds) use exact-score prediction for GOTR bonus (+5).
  // For WC group stage AND EPL, Draw is a valid pick (WC knockouts disable it).
  const usesExactScore = isWC || isOddsBased;
  const allowsDraw = isOddsBased || (isWC && (selectedRound || 0) < 4);

  useEffect(() => {
    fetchInitialData();
  }, [sport]);

  // Warn users on navigate-away if they've made picks that aren't finalised.
  // Reinforces the "finalise or lose them" rule so no one is surprised when
  // they come back and find their selections gone.
  useEffect(() => {
    const hasUnfinalisedPicks = () => {
      if (finaliseStatus?.is_finalised) return false;
      const selectedFxIds = Object.keys(tips).filter(fid => tips[fid]?.predicted_winner);
      if (selectedFxIds.length === 0) return false;
      // Compare against what's already saved from the last fetch (myTips).
      // If every current selection matches a saved+finalised tip, nothing to warn about.
      const finalisedByFx = {};
      (myTips || []).forEach(t => { if (t.is_finalised) finalisedByFx[t.fixture_id] = t; });
      return selectedFxIds.some(fid => {
        const saved = finalisedByFx[fid];
        return !saved || saved.predicted_winner !== tips[fid].predicted_winner;
      });
    };
    const onBeforeUnload = (e) => {
      if (!hasUnfinalisedPicks()) return;
      e.preventDefault();
      e.returnValue = "Your tips aren't saved yet — click 'Save & Finalise Tips' first.";
      return e.returnValue;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [tips, myTips, finaliseStatus]);

  useEffect(() => {
    if (selectedRound !== null && selectedCompetition) {
      fetchFixturesAndTips();
    }
  }, [selectedRound, selectedCompetition]);

  const fetchInitialData = async () => {
    try {
      const [roundsRes, compsRes, logosRes, currentRes] = await Promise.all([
        axios.get(`${API_URL}/api/fixtures/${sport}/rounds?season=2026`, { withCredentials: true }),
        axios.get(`${API_URL}/api/competitions?sport=${sport}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/teams/${sport}/logos`, { withCredentials: true }),
        axios.get(`${API_URL}/api/current-round/${sport}`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      
      setRounds(roundsRes.data);
      setCompetitions(compsRes.data);
      setTeamLogos(logosRes.data);
      
      // For WC: load FIFA world rankings for inline display next to team names
      if (sport === 'WC') {
        try {
          const lr = await axios.get(`${API_URL}/api/ladder/WC`, { withCredentials: true });
          const map = {};
          (lr.data?.ladder || []).forEach(row => {
            if (row.team && row.world_ranking != null) map[row.team] = row.world_ranking;
          });
          setWorldRankings(map);
        } catch (_) { /* non-fatal */ }
      }
      
      // Default to the current round (first round still in play), or fall back to the first round in the list
      if (roundsRes.data.length > 0) {
        const currentRound = currentRes?.data?.round_number;
        const defaultRound = roundsRes.data.includes(currentRound)
          ? currentRound
          : roundsRes.data[0];
        setSelectedRound(defaultRound);
      }
      if (compsRes.data.length > 0) {
        setSelectedCompetition(compsRes.data[0].competition_id);
      }
    } catch (err) {
      console.error('Error fetching initial data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFixturesAndTips = async () => {
    try {
      const [fixturesRes, tipsRes, summaryRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/fixtures/${sport}?round_number=${selectedRound}&season=2026`, { withCredentials: true }),
        // Anonymous users will get 401 here — fall back to empty list
        axios.get(`${API_URL}/api/tips?competition_id=${selectedCompetition}&round_number=${selectedRound}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/tips/round-summary?competition_id=${selectedCompetition}&sport=${sport}&round_number=${selectedRound}`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/tips/finalisation-status?competition_id=${selectedCompetition}&sport=${sport}&round_number=${selectedRound}`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      
      setFixtures(fixturesRes.data);
      setMyTips(tipsRes.data);
      setRoundSummary(summaryRes.data);
      setFinaliseStatus(statusRes.data);
      
      // Map existing tips to state
      const tipsMap = {};
      tipsRes.data.forEach(tip => {
        tipsMap[tip.fixture_id] = {
          predicted_winner: tip.predicted_winner,
          margin_prediction: tip.margin_prediction,
          goalkicker_prediction: tip.goalkicker_prediction,
          home_score_prediction: tip.home_score_prediction,
          away_score_prediction: tip.away_score_prediction,
        };
      });
      setTips(tipsMap);
    } catch (err) {
      console.error('Error fetching fixtures:', err);
    }
  };

  const saveDraftTip = async () => {
    // Retained as a no-op for any legacy caller. Drafts are never persisted —
    // tips only hit the DB via /api/tips/finalise when the user submits the
    // whole round. See handleSelectWinner + handleFinaliseAll.
    return;
  };

  const handleSelectWinner = (fixtureId, team) => {
    // Local-only until the user clicks "Save & Finalise". Nothing hits the
    // DB, so if they close the tab or navigate away, the selection vanishes
    // — matches the user rule: no draft persistence, finalise-or-nothing.
    setTips(prev => ({
      ...prev,
      [fixtureId]: {
        ...prev[fixtureId],
        predicted_winner: team
      }
    }));
  };

  const handleMarginChange = (fixtureId, margin) => {
    setTips(prev => ({
      ...prev,
      [fixtureId]: {
        ...prev[fixtureId],
        margin_prediction: parseInt(margin) || null
      }
    }));
  };

  const handleScoreChange = (fixtureId, side, value) => {
    const key = side === 'home' ? 'home_score_prediction' : 'away_score_prediction';
    const parsed = value === '' ? null : Math.max(0, parseInt(value, 10));
    setTips(prev => ({
      ...prev,
      [fixtureId]: {
        ...prev[fixtureId],
        [key]: Number.isNaN(parsed) ? null : parsed,
      }
    }));
  };

  const handleGoalkickerChange = (fixtureId, value) => {
    setTips(prev => ({
      ...prev,
      [fixtureId]: {
        ...prev[fixtureId],
        goalkicker_prediction: value || null
      }
    }));
  };

  const handleSubmitTip = async (fixture) => {
    if (!tips[fixture.fixture_id]?.predicted_winner) {
      toast.error('Please select a winner');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fixture.fixture_id]: true }));

    try {
      await axios.post(
        `${API_URL}/api/tips`,
        {
          fixture_id: fixture.fixture_id,
          competition_id: selectedCompetition,
          predicted_winner: tips[fixture.fixture_id].predicted_winner,
          margin_prediction: tips[fixture.fixture_id].margin_prediction || null,
          goalkicker_prediction: tips[fixture.fixture_id].goalkicker_prediction || null
        },
        { withCredentials: true }
      );
      
      toast.success('Tip saved!');
      fetchFixturesAndTips();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to save tip';
      toast.error(message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fixture.fixture_id]: false }));
    }
  };

  const handleFinaliseAll = async () => {
    // Client-side validation
    const missing = fixtures
      .filter(f => !tips[f.fixture_id]?.predicted_winner)
      .map(f => `${f.home_team} v ${f.away_team}`);
    if (missing.length > 0) {
      toast.error(`Pick a winner for every match. Missing: ${missing[0]}${missing.length > 1 ? ` (+${missing.length - 1} more)` : ''}`);
      return;
    }
    const gotrFixtures = fixtures.filter(f => f.is_game_of_round);
    for (const gotr of gotrFixtures) {
      const gtip = tips[gotr.fixture_id] || {};
      if (sport === 'WC' || sport === 'EPL' || sport === 'AL') {
        if (gtip.home_score_prediction == null || gtip.away_score_prediction == null) {
          toast.error(`Enter an exact score for the Game of the Round (${gotr.home_team} v ${gotr.away_team})`);
          return;
        }
      } else {
        if (gtip.margin_prediction == null || gtip.margin_prediction === '') {
          toast.error('Enter a winning margin for the Game of the Round');
          return;
        }
        if (sport === 'AFL' && !gotr.is_finals && !(gtip.goalkicker_prediction || '').trim()) {
          toast.error('Enter a leading goalkicker for the Game of the Round');
          return;
        }
      }
    }

    setFinalising(true);
    try {
      const payload = {
        competition_id: selectedCompetition,
        sport,
        round_number: selectedRound,
        tips: fixtures.map(f => ({
          fixture_id: f.fixture_id,
          predicted_winner: tips[f.fixture_id].predicted_winner,
          margin_prediction: tips[f.fixture_id].margin_prediction ?? null,
          goalkicker_prediction: tips[f.fixture_id].goalkicker_prediction || null,
          home_score_prediction: tips[f.fixture_id].home_score_prediction ?? null,
          away_score_prediction: tips[f.fixture_id].away_score_prediction ?? null,
        })),
      };
      const res = await axios.post(`${API_URL}/api/tips/finalise`, payload, { withCredentials: true });
      if (res.data.email_sent) {
        toast.success('Tips finalised — confirmation email sent. Good luck!');
      } else {
        toast.success('Tips finalised — good luck this round!');
      }
      fetchFixturesAndTips();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to finalise tips');
    } finally {
      setFinalising(false);
    }
  };

  const isMatchStarted = (fixture) => {
    const matchTime = new Date(fixture.match_time);
    return new Date() >= matchTime;
  };

  const formatMatchTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getExistingTip = (fixtureId) => {
    return myTips.find(t => t.fixture_id === fixtureId);
  };

  // EPL only: aggregate potential score across the tipster's current picks.
  // Uses local `tips` selections first, falls back to any already-saved tip.
  // Formula matches the backend: winner_odds × (GOTR ? 2 : 1) + (exact_score ? 3 : 0).
  const potentialTotal = useMemo(() => {
    if (!isOddsBased || !fixtures.length) return null;
    let total = 0;
    let anyPick = false;
    for (const fx of fixtures) {
      const draft = tips[fx.fixture_id] || {};
      const existing = getExistingTip(fx.fixture_id) || {};
      const picked = draft.predicted_winner ?? existing.predicted_winner;
      if (!picked) continue;
      anyPick = true;
      let odds = null;
      if (picked === 'Draw') odds = fx.draw_odds;
      else if (picked === fx.home_team) odds = fx.home_odds;
      else if (picked === fx.away_team) odds = fx.away_odds;
      if (odds == null) continue;
      let pts = Number(odds);
      if (fx.is_game_of_round) pts *= 2;
      const hp = draft.home_score_prediction ?? existing.home_score_prediction;
      const ap = draft.away_score_prediction ?? existing.away_score_prediction;
      if (hp !== undefined && hp !== null && hp !== '' && ap !== undefined && ap !== null && ap !== '') {
        pts += 3;
      }
      total += pts;
    }
    return anyPick ? Math.round(total * 100) / 100 : 0;
  }, [isOddsBased, fixtures, tips, myTips]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Final leaderboard for closed competitions (AFL 2026, WC 2026) */}
        <FinalLeaderboardCard sport={sport} currentUserId={user?.user_id} />

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span 
              className="w-4 h-4 rounded-sm"
              style={{ backgroundColor: sportColor }}
            />
            <span className={sportBadgeClass}>{sport} 2026</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter" data-testid="tipping-heading">
            {sport} Tipping
          </h1>
          {isOddsBased && (
            <div className="mt-3">
              <OddsRefreshChip />
            </div>
          )}
        </div>

        {/* Sign-in CTA for anonymous viewers */}
        {!user && (
          <div className="tipster-card border-[#007AFF]/30 bg-[#007AFF]/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6" data-testid="anon-signin-cta">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#007AFF] font-semibold mb-1">Viewing as guest</div>
              <p className="text-sm text-gray-700">Sign in to submit tips and join the leaderboard.</p>
            </div>
            <Link to="/login">
              <Button size="sm" className="bg-[#007AFF] hover:bg-[#3395FF] text-white" data-testid="tipping-anon-signin-btn">
                Sign In
              </Button>
            </Link>
          </div>
        )}

        {/* Finalise-or-nothing banner (signed-in only, before finalise) */}
        {user && !finaliseStatus?.is_finalised && (
          <div className="mb-6 rounded-sm border border-[#FFCC00] bg-[#FFF9E5] text-[#7A5B00] px-4 py-3 flex items-start gap-3" data-testid="finalise-reminder">
            <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <strong>Your picks aren&apos;t saved until you finalise.</strong> Click <em>Save &amp; Finalise Tips</em> before you leave the page — anything not finalised will be discarded, and any matches you miss get auto-picked to the away team at the deadline.
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="w-full sm:w-48">
            <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Round</label>
            <Select value={selectedRound?.toString()} onValueChange={(v) => setSelectedRound(parseInt(v))}>
              <SelectTrigger className="bg-white border-gray-200" data-testid="round-select">
                <SelectValue placeholder="Select Round" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                {rounds.map((round) => (
                  <SelectItem key={round} value={round.toString()}>
                    {roundLabel(sport, round)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-64">
            <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Competition</label>
            <Select value={selectedCompetition} onValueChange={setSelectedCompetition}>
              <SelectTrigger className="bg-white border-gray-200" data-testid="competition-select">
                <SelectValue placeholder="Select Competition" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                {competitions.map((comp) => (
                  <SelectItem key={comp.competition_id} value={comp.competition_id}>
                    {comp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* No competition warning (only when authed but not joined yet) */}
        {user && !selectedCompetition && (
          <div className="tipster-card text-center py-10 mb-8">
            <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No competition available yet — please contact an admin.</p>
          </div>
        )}

        {/* Two-column layout: fixtures + ladder */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
          <div>
        {/* Fixtures List */}
        {selectedCompetition && (
          <div className="space-y-4">
            {/* Round Scoring Summary */}
            {roundSummary && roundSummary.total_tipped > 0 && roundSummary.completed_matches > 0 && (
              <div className="tipster-card border-[#007AFF]/30 mb-2" data-testid="round-summary">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">
                      {roundLabel(sport, selectedRound)} Summary
                    </div>
                    <div className="flex items-center gap-6">
                      <div>
                        <span className="text-3xl font-black text-[#007AFF]">{Number(roundSummary.total_points || 0).toFixed(2)}</span>
                        <span className="text-sm text-gray-500 ml-1">pts</span>
                      </div>
                      <div className="h-8 w-px bg-gray-200" />
                      <div>
                        <span className="text-lg font-bold text-[#34C759]">{roundSummary.correct_tips}</span>
                        <span className="text-sm text-gray-500">/{roundSummary.completed_matches} correct</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wider text-gray-400">Tipped</div>
                    <div className="text-lg font-bold">{roundSummary.total_tipped}/{roundSummary.total_matches}</div>
                  </div>
                </div>
              </div>
            )}

            {fixtures.length === 0 ? (
              <div className="tipster-card text-center py-10">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No fixtures for this round</p>
              </div>
            ) : (
              fixtures.map((fixture, index) => {
                const existingTip = getExistingTip(fixture.fixture_id);
                const currentTip = tips[fixture.fixture_id] || {};
                const started = isMatchStarted(fixture);
                const readOnly = !user;
                const roundLocked = !!finaliseStatus?.locked;
                // EPL: block tipping until bookmaker odds are loaded.
                const oddsPending = isOddsBased && (
                  fixture.home_odds == null || fixture.draw_odds == null || fixture.away_odds == null
                );
                const locked = started || readOnly || roundLocked || oddsPending;
                const isGotr = fixture.is_game_of_round;

                return (
                  <div 
                    key={fixture.fixture_id} 
                    className={`tipster-card animate-fade-up stagger-${Math.min(index + 1, 5)} ${isGotr ? 'border-[#007AFF]/50' : ''}`}
                    data-testid={`fixture-card-${fixture.fixture_id}`}
                    data-fixture-id={fixture.fixture_id}
                    onMouseEnter={() => setHoveredFixtureId(fixture.fixture_id)}
                    onMouseLeave={() => setHoveredFixtureId(prev => prev === fixture.fixture_id ? null : prev)}
                  >
                    {/* Match Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {isGotr && (
                          <span className="tipster-badge flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            Game of the Round
                          </span>
                        )}
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          <MapPin className="w-4 h-4" />
                          {fixture.venue}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Clock className="w-4 h-4" />
                        {formatMatchTime(fixture.match_time)}
                      </div>
                      {!fixture.is_completed && <InPlayBadge match={fixture} />}
                    </div>

                    {/* Points pending banner — EPL fixtures where prices haven't been posted yet */}
                    {oddsPending && (() => {
                      // Inside the 14-day window a missing price almost always
                      // means the provider hasn't posted markets for this
                      // match yet (some cup-affected weeks price late) or the
                      // odds API is having an outage. Outside the window it's
                      // simply the standard reveal rule.
                      const mt = fixture.match_time ? new Date(fixture.match_time) : null;
                      const hoursOut = mt ? (mt - new Date()) / 36e5 : Infinity;
                      const insideWindow = hoursOut <= 24 * 14;
                      return (
                        <div
                          className="flex items-center gap-2 rounded-sm bg-[#37003C]/5 border border-[#37003C]/20 text-[#37003C] text-xs sm:text-sm px-3 py-2 mb-4"
                          data-testid={`epl-odds-pending-${fixture.fixture_id}`}
                        >
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          <span>
                            {insideWindow
                              ? <><strong>Points pending.</strong> Markets for this match haven&apos;t been posted yet — refresh in a few hours.</>
                              : <><strong>Points pending.</strong> Tips unlock once points are posted (~2 weeks before kickoff).</>}
                          </span>
                        </div>
                      );
                    })()}

                    {/* Teams Selection */}
                    <div className="grid grid-cols-[1fr,auto,1fr] gap-1.5 sm:gap-4 items-center mb-4">
                      {/* Home Team */}
                      {(() => {
                        const isMatchWinner = fixture.is_completed && fixture.home_score != null && fixture.away_score != null && fixture.home_score > fixture.away_score;
                        const isMatchLoser = fixture.is_completed && fixture.home_score != null && fixture.away_score != null && fixture.home_score < fixture.away_score;
                        const userTippedThis = existingTip?.predicted_winner === fixture.home_team;
                        const tipCorrect = fixture.is_completed && userTippedThis && isMatchWinner;
                        const tipWrong = fixture.is_completed && userTippedThis && !isMatchWinner;
                        return (
                          <button
                            onClick={() => !locked && handleSelectWinner(fixture.fixture_id, fixture.home_team)}
                            disabled={locked}
                            className={`tip-selection p-2 sm:p-4 rounded-sm text-left flex items-center gap-2 sm:gap-3 min-w-0 ${
                              !fixture.is_completed && currentTip.predicted_winner === fixture.home_team ? 'selected' : ''
                            } ${locked && !fixture.is_completed ? 'opacity-60 cursor-not-allowed' : ''} ${
                              tipCorrect ? '!border-[#34C759] !bg-[#34C759]/8' : ''
                            } ${tipWrong ? '!border-[#FF3B30] !bg-[#FF3B30]/8' : ''
                            } ${fixture.is_completed && !userTippedThis && isMatchLoser ? 'opacity-50' : ''
                            } ${started ? 'cursor-default' : ''}`}
                            data-testid={`tip-home-${fixture.fixture_id}`}
                          >
                            <div className="flex-shrink-0">
                              <TeamLogo 
                                teamName={fixture.home_team} 
                                logoUrl={teamLogos[fixture.home_team]}
                                size={32}
                                className="sm:hidden"
                              />
                              <TeamLogo 
                                teamName={fixture.home_team} 
                                logoUrl={teamLogos[fixture.home_team]}
                                size={48}
                                className="hidden sm:block"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`font-bold text-sm sm:text-lg truncate ${
                                tipCorrect ? 'text-[#34C759]' : ''
                              } ${tipWrong ? 'text-[#FF3B30]' : ''
                              } ${fixture.is_completed && !userTippedThis && isMatchLoser ? 'text-gray-400' : ''}`}>
                                {fixture.home_team}
                                {isWC && worldRankings[fixture.home_team] != null && (
                                  <span className="ml-1.5 text-[10px] font-bold text-gray-400 align-middle tabular-nums" title={`FIFA World Ranking #${worldRankings[fixture.home_team]}`}>
                                    #{worldRankings[fixture.home_team]}
                                  </span>
                                )}
                              </div>
                              <div className="text-gray-500 text-xs sm:text-sm flex items-center gap-2">
                                <span>Home</span>
                                {isOddsBased && fixture.home_odds != null && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#37003C]/10 text-[#37003C] font-bold text-[10px] sm:text-[11px] tabular-nums"
                                    title="Points if correct"
                                    data-testid={`epl-odds-home-${fixture.fixture_id}`}
                                  >
                                    {fixture.home_odds.toFixed(2)} pts
                                  </span>
                                )}
                              </div>
                            </div>
                            {fixture.is_completed && fixture.home_score != null && (
                              <div className={`text-xl sm:text-3xl font-black tabular-nums flex-shrink-0 ${
                                isMatchWinner ? 'text-[#1A1A1A]' : 'text-gray-400'
                              }`}>
                                {fixture.home_score}
                              </div>
                            )}
                          </button>
                        );
                      })()}

                      {/* VS / Score divider */}
                      <div className="text-center flex flex-col items-center gap-1">
                        {fixture.is_completed ? (
                          <>
                            <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-gray-100 text-gray-500">FT</span>
                            {existingTip && (() => {
                              const winner = fixture.home_score > fixture.away_score ? fixture.home_team : fixture.away_team;
                              const gotIt = existingTip.predicted_winner === winner;
                              const pts = existingTip.points_earned || 0;
                              return (
                                <div className="mt-1 flex flex-col items-center">
                                  {gotIt ? (
                                    <Check className="w-5 h-5 text-[#34C759] mx-auto" />
                                  ) : (
                                    <X className="w-5 h-5 text-[#FF3B30] mx-auto" />
                                  )}
                                  {pts > 0 && (
                                    <span className="text-xs font-bold text-[#007AFF] mt-0.5">+{pts}</span>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <>
                            <div className="text-gray-400 text-sm uppercase tracking-wider">vs</div>
                            {/* Draw option — WC group stage AND all EPL fixtures.
                                WC knockouts must have a winner. */}
                            {allowsDraw && (
                              <button
                                type="button"
                                onClick={() => !locked && handleSelectWinner(fixture.fixture_id, 'Draw')}
                                disabled={locked}
                                className={`mt-1 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider border transition-colors ${
                                  currentTip.predicted_winner === 'Draw'
                                    ? 'text-white'
                                    : `bg-white text-gray-500 border-gray-200`
                                } ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}
                                style={
                                  currentTip.predicted_winner === 'Draw'
                                    ? { backgroundColor: sportColor, borderColor: sportColor }
                                    : {}
                                }
                                data-testid={`tip-draw-${fixture.fixture_id}`}
                              >
                                Draw
                                {isOddsBased && fixture.draw_odds != null && (
                                  <span className="block text-[9px] font-black tabular-nums mt-0.5 opacity-90">
                                    {fixture.draw_odds.toFixed(2)}
                                  </span>
                                )}
                              </button>
                            )}
                            {existingTip && !usesExactScore && (
                              <div className="mt-1">
                                <Check className="w-4 h-4 text-[#007AFF] mx-auto" />
                              </div>
                            )}
                            {existingTip && allowsDraw && existingTip.predicted_winner === 'Draw' && (
                              <span className="text-[10px] font-bold mt-0.5" style={{ color: sportColor }}>PICKED</span>
                            )}
                          </>
                        )}
                      </div>

                      {/* Away Team */}
                      {(() => {
                        const isMatchWinner = fixture.is_completed && fixture.home_score != null && fixture.away_score != null && fixture.away_score > fixture.home_score;
                        const isMatchLoser = fixture.is_completed && fixture.home_score != null && fixture.away_score != null && fixture.away_score < fixture.home_score;
                        const userTippedThis = existingTip?.predicted_winner === fixture.away_team;
                        const tipCorrect = fixture.is_completed && userTippedThis && isMatchWinner;
                        const tipWrong = fixture.is_completed && userTippedThis && !isMatchWinner;
                        return (
                          <button
                            onClick={() => !locked && handleSelectWinner(fixture.fixture_id, fixture.away_team)}
                            disabled={locked}
                            className={`tip-selection p-2 sm:p-4 rounded-sm text-right flex items-center gap-2 sm:gap-3 flex-row-reverse min-w-0 ${
                              !fixture.is_completed && currentTip.predicted_winner === fixture.away_team ? 'selected' : ''
                            } ${locked && !fixture.is_completed ? 'opacity-60 cursor-not-allowed' : ''} ${
                              tipCorrect ? '!border-[#34C759] !bg-[#34C759]/8' : ''
                            } ${tipWrong ? '!border-[#FF3B30] !bg-[#FF3B30]/8' : ''
                            } ${fixture.is_completed && !userTippedThis && isMatchLoser ? 'opacity-50' : ''
                            } ${started ? 'cursor-default' : ''}`}
                            data-testid={`tip-away-${fixture.fixture_id}`}
                          >
                            <div className="flex-shrink-0">
                              <TeamLogo 
                                teamName={fixture.away_team} 
                                logoUrl={teamLogos[fixture.away_team]}
                                size={32}
                                className="sm:hidden"
                              />
                              <TeamLogo 
                                teamName={fixture.away_team} 
                                logoUrl={teamLogos[fixture.away_team]}
                                size={48}
                                className="hidden sm:block"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`font-bold text-sm sm:text-lg truncate ${
                                tipCorrect ? 'text-[#34C759]' : ''
                              } ${tipWrong ? 'text-[#FF3B30]' : ''
                              } ${fixture.is_completed && !userTippedThis && isMatchLoser ? 'text-gray-400' : ''}`}>
                                {fixture.away_team}
                                {isWC && worldRankings[fixture.away_team] != null && (
                                  <span className="ml-1.5 text-[10px] font-bold text-gray-400 align-middle tabular-nums" title={`FIFA World Ranking #${worldRankings[fixture.away_team]}`}>
                                    #{worldRankings[fixture.away_team]}
                                  </span>
                                )}
                              </div>
                              <div className="text-gray-500 text-xs sm:text-sm flex items-center gap-2 justify-end">
                                {isOddsBased && fixture.away_odds != null && (
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#37003C]/10 text-[#37003C] font-bold text-[10px] sm:text-[11px] tabular-nums"
                                    title="Points if correct"
                                    data-testid={`epl-odds-away-${fixture.fixture_id}`}
                                  >
                                    {fixture.away_odds.toFixed(2)} pts
                                  </span>
                                )}
                                <span>Away</span>
                              </div>
                            </div>
                            {fixture.is_completed && fixture.away_score != null && (
                              <div className={`text-xl sm:text-3xl font-black tabular-nums flex-shrink-0 ${
                                isMatchWinner ? 'text-[#1A1A1A]' : 'text-gray-400'
                              }`}>
                                {fixture.away_score}
                              </div>
                            )}
                          </button>
                        );
                      })()}
                    </div>

                    {/* Form & H2H Insights */}
                    <MatchInsights
                      sport={sport}
                      homeTeam={fixture.home_team}
                      awayTeam={fixture.away_team}
                      fixtureId={fixture.fixture_id}
                      roundNumber={fixture.round_number}
                    />

                    {/* Live / post-match stats (only if started or completed) */}
                    {(fixture.is_completed || started) && (
                      <MatchLiveStats
                        sport={sport}
                        homeTeam={fixture.home_team}
                        awayTeam={fixture.away_team}
                        roundNumber={fixture.round_number}
                      />
                    )}

                    {/* Game of the Round - Bonus Predictions */}
                    {isGotr && !started && !readOnly && (
                      <div className="bg-[#007AFF]/10 border border-[#007AFF]/20 rounded-sm p-4 mb-4">
                        <label className="text-xs uppercase tracking-wider text-[#007AFF] mb-3 block">
                          Game of the Round — Bonus Predictions
                        </label>
                        <div className="space-y-3">
                          {usesExactScore ? (
                            <div className="flex flex-wrap items-end gap-3">
                              <div>
                                <div className="text-xs text-gray-500 mb-1 truncate max-w-[8rem]">{fixture.home_team}</div>
                                <Input
                                  type="number"
                                  min="0"
                                  max="20"
                                  value={currentTip.home_score_prediction ?? ''}
                                  onChange={(e) => handleScoreChange(fixture.fixture_id, 'home', e.target.value)}
                                  disabled={locked}
                                  className="w-20 bg-white border-gray-200 text-center tabular-nums disabled:opacity-50 disabled:cursor-not-allowed"
                                  placeholder="0"
                                  data-testid={`${sport.toLowerCase()}-home-score-${fixture.fixture_id}`}
                                />
                              </div>
                              <div className="text-2xl font-black text-gray-400 mb-1">–</div>
                              <div>
                                <div className="text-xs text-gray-500 mb-1 truncate max-w-[8rem]">{fixture.away_team}</div>
                                <Input
                                  type="number"
                                  min="0"
                                  max="20"
                                  value={currentTip.away_score_prediction ?? ''}
                                  onChange={(e) => handleScoreChange(fixture.fixture_id, 'away', e.target.value)}
                                  disabled={locked}
                                  className="w-20 bg-white border-gray-200 text-center tabular-nums disabled:opacity-50 disabled:cursor-not-allowed"
                                  placeholder="0"
                                  data-testid={`${sport.toLowerCase()}-away-score-${fixture.fixture_id}`}
                                />
                              </div>
                              <span className="text-gray-400 text-xs mb-2 ml-2">
                                {isOddsBased
                                  ? 'Winner points × 2 · Exact score +3 pts'
                                  : isWC && (fixture.round_number || 0) >= 4
                                    ? 'Winner +2 pts · Exact score +5 pts'
                                    : 'Winner +4 pts · Exact score +5 pts'}
                              </span>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-4">
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">Winning Margin</div>
                                  <Input
                                    type="number"
                                    min="0"
                                    max="200"
                                    value={currentTip.margin_prediction || ''}
                                    onChange={(e) => handleMarginChange(fixture.fixture_id, e.target.value)}
                                    disabled={locked}
                                    className="w-28 bg-white border-gray-200 focus:border-[#007AFF] disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder="Points"
                                    data-testid={`margin-input-${fixture.fixture_id}`}
                                  />
                                </div>
                                <span className="text-gray-400 text-xs mt-5">
                                  +1 closest | +5 exact
                                </span>
                              </div>
                              {sport === 'AFL' && !fixture.is_finals && (
                                <div className="flex items-center gap-4">
                                  <div>
                                    <div className="text-xs text-gray-500 mb-1">Leading Goalkicker</div>
                                    <Input
                                      type="text"
                                      value={currentTip.goalkicker_prediction || ''}
                                      onChange={(e) => handleGoalkickerChange(fixture.fixture_id, e.target.value)}
                                      disabled={locked}
                                      className="w-52 bg-white border-gray-200 focus:border-[#007AFF] disabled:opacity-50 disabled:cursor-not-allowed"
                                      placeholder="Player name"
                                      data-testid={`goalkicker-input-${fixture.fixture_id}`}
                                    />
                                  </div>
                                  <span className="text-gray-400 text-xs mt-5">
                                    +1 pt if correct
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Show GOTR predictions on completed matches */}
                    {isGotr && started && existingTip && (existingTip.margin_prediction != null || existingTip.goalkicker_prediction || existingTip.home_score_prediction != null) && (
                      <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 mb-4">
                        <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">Your GOTR Predictions</div>
                        <div className="flex flex-wrap gap-6 text-sm">
                          {usesExactScore && existingTip.home_score_prediction != null && existingTip.away_score_prediction != null && (
                            <div>
                              <span className="text-gray-500">Score: </span>
                              <span className="font-semibold">{fixture.home_team} {existingTip.home_score_prediction} – {existingTip.away_score_prediction} {fixture.away_team}</span>
                              {fixture.is_completed && fixture.home_score != null && (
                                <span className="text-gray-400 ml-1">
                                  (actual: {fixture.home_score}–{fixture.away_score})
                                </span>
                              )}
                            </div>
                          )}
                          {!usesExactScore && existingTip.margin_prediction != null && (
                            <div>
                              <span className="text-gray-500">Margin: </span>
                              <span className="font-semibold">{existingTip.margin_prediction} pts</span>
                              {fixture.is_completed && fixture.home_score != null && (
                                <span className="text-gray-400 ml-1">
                                  (actual: {Math.abs(fixture.home_score - fixture.away_score)})
                                </span>
                              )}
                            </div>
                          )}
                          {existingTip.goalkicker_prediction && (
                            <div>
                              <span className="text-gray-500">Goalkicker: </span>
                              <span className="font-semibold">{existingTip.goalkicker_prediction}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Match Started Notice */}
                    {started && !fixture.is_completed && (
                      <div className="text-center text-gray-400 text-sm uppercase tracking-wider">
                        Match in progress - tipping locked
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Finalise All Tips — single bottom action */}
        {selectedCompetition && fixtures.length > 0 && user && !finaliseStatus?.locked && (() => {
          const oddsMissingCount = isOddsBased
            ? fixtures.filter(f => f.home_odds == null || f.draw_odds == null || f.away_odds == null).length
            : 0;
          if (oddsMissingCount > 0) {
            return (
              <div
                className="tipster-card mt-6 bg-[#37003C]/5 border-[#37003C]/30 text-center"
                data-testid="epl-odds-blocking-finalise"
              >
                <div className="text-sm font-bold uppercase tracking-wider text-[#37003C] mb-1">
                  Waiting on points
                </div>
                <p className="text-gray-600 text-sm">
                  {oddsMissingCount === fixtures.length
                    ? "Points aren't available for this round yet — tips can be submitted from around 7 days before the first kickoff."
                    : `Points are still pending for ${oddsMissingCount} of ${fixtures.length} matches. You'll be able to finalise once every game has points posted.`}
                </p>
              </div>
            );
          }
          return (
            <FinaliseBar
              sport={sport}
              roundNumber={selectedRound}
              deadline={finaliseStatus?.deadline}
              onFinalise={handleFinaliseAll}
              finalising={finalising}
              potentialTotal={isOddsBased ? potentialTotal : null}
            />
          );
        })()}
        {selectedCompetition && fixtures.length > 0 && user && finaliseStatus?.is_finalised && (
          <div className="tipster-card mt-6 bg-green-50 border-green-200 text-center" data-testid="tipping-finalised-banner">
            <div className="text-sm font-bold uppercase tracking-wider text-green-700 mb-1">Tips Finalised</div>
            <p className="text-gray-600 text-sm">Your tips for this round are locked in. Good luck!</p>
          </div>
        )}
        {selectedCompetition && fixtures.length > 0 && user && finaliseStatus?.locked && !finaliseStatus?.is_finalised && (
          <div className="tipster-card mt-6 bg-amber-50 border-amber-200 text-center" data-testid="tipping-deadline-banner">
            <div className="text-sm font-bold uppercase tracking-wider text-amber-700 mb-1">Tipping Closed</div>
            <p className="text-gray-600 text-sm">The 5pm Thursday deadline has passed for this round.</p>
          </div>
        )}
          </div>

          {/* Sidebar ladder */}
          <Ladder
            sport={sport}
            teamLogos={teamLogos}
            highlightTeams={(() => {
              const set = new Set();
              if (hoveredFixtureId) {
                const f = fixtures.find(x => x.fixture_id === hoveredFixtureId);
                if (f) { set.add(f.home_team); set.add(f.away_team); }
              }
              Object.values(tips || {}).forEach(t => { if (t?.predicted_winner) set.add(t.predicted_winner); });
              return set;
            })()}
            onTeamClick={(team) => {
              const f = fixtures.find(x => x.home_team === team || x.away_team === team);
              if (!f) return;
              const el = document.querySelector(`[data-fixture-id="${f.fixture_id}"]`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
          />
        </div>

        {/* Leaderboard Link */}
        <div className="mt-8 text-center">
          <Link to={`/leaderboard/${sport}`}>
            <Button variant="outline" className="border-gray-200 hover:border-gray-300 hover:bg-gray-50" data-testid="view-leaderboard-btn">
              <Trophy className="w-4 h-4 mr-2" />
              View {sport} Leaderboard
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
